import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { trackPageView } from "@/lib/analytics";

import LandingPage from "@/pages/LandingPage";
import LandingPageEssence from "@/pages/LandingPageEssence";
import AuthPage from "@/pages/AuthPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import LegalPage from "@/pages/LegalPage";
import LegalFooter from "@/components/LegalFooter";
import Dashboard from "@/pages/Dashboard";
import Lessons from "@/pages/Lessons";
import LessonDetail from "@/pages/LessonDetail";
import Exercises from "@/pages/Exercises";
import ExerciseDetail from "@/pages/ExerciseDetail";
import ChatAI from "@/pages/ChatAI";
import Subscription from "@/pages/Subscription";
import Journal from "@/pages/Journal";
import ProgressPage from "@/pages/ProgressPage";
import Profile from "@/pages/Profile";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminPanel from "@/pages/AdminPanel";
import DailyFeelingHistory from "@/pages/DailyFeelingHistory";
import FeedbackWidget from "@/components/FeedbackWidget";
import ChatFloatingButton from "@/components/ChatFloatingButton";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname, document.title);
  }, [pathname]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState("es");

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
        setLanguage(response.data.language || "es");
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  };

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
    setLanguage(userData.language || "es");
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    toast.success(language === "es" ? "Sesión cerrada" : "Sessió tancada");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <div className="animate-pulse text-teal-300 text-xl">Cargando...</div>
      </div>
    );
  }

  const getToken = () => localStorage.getItem("token");

  return (
    <AuthContext.Provider value={{ user, login, logout, language, setLanguage, token: getToken() }}>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          {user && <EmailVerificationBanner />}
          <Routes>
            <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
            <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <AuthPage />} />
            <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
            <Route path="/reset-password" element={user ? <Navigate to="/dashboard" /> : <ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/auth" />} />
            <Route path="/lessons" element={user ? <Lessons /> : <Navigate to="/auth" />} />
            <Route path="/lessons/:id" element={user ? <LessonDetail /> : <Navigate to="/auth" />} />
            <Route path="/exercises" element={user ? <Exercises /> : <Navigate to="/auth" />} />
            <Route path="/exercises/:exerciseId" element={user ? <ExerciseDetail /> : <Navigate to="/auth" />} />
            <Route path="/chat" element={user ? <ChatAI /> : <Navigate to="/auth" />} />
            <Route path="/subscription" element={user ? <Subscription /> : <Navigate to="/auth" />} />
            <Route path="/subscription/success" element={user ? <Subscription /> : <Navigate to="/auth" />} />
            <Route path="/subscription/cancel" element={user ? <Subscription /> : <Navigate to="/auth" />} />
            <Route path="/journal" element={user ? <Journal /> : <Navigate to="/auth" />} />
            <Route path="/progress" element={user ? <ProgressPage /> : <Navigate to="/auth" />} />
            <Route path="/profile" element={user ? <Profile /> : <Navigate to="/auth" />} />
            <Route path="/admin/feedback" element={user ? <AdminFeedback /> : <Navigate to="/auth" />} />
            <Route path="/admin" element={user ? <AdminPanel /> : <Navigate to="/auth" />} />
            <Route path="/admin/users" element={user ? <AdminPanel /> : <Navigate to="/auth" />} />
            <Route path="/feeling-history" element={user ? <DailyFeelingHistory /> : <Navigate to="/auth" />} />
            <Route path="/preview-essence" element={<LandingPageEssence />} />
          </Routes>
          {user && (
            <>
              <LegalFooter />
              <FeedbackWidget />
              <ChatFloatingButton />
            </>
          )}
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
export { API };